import React, { useMemo } from 'react';
import { Box, Paper, Skeleton, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useGetAllScheduleQuery } from 'store/slices/apis/keyScheduleSlice';
import Highcharts from "highcharts/highcharts-gantt";
import HighchartsReact from "highcharts-react-official";

const StyledTableHead = styled(TableCell)(({ theme }) => ({
    fontWeight: "400", fontSize: '14px', color: '#37352F', padding: '10px 14px 10px 14px'
}));

const StyledTableRow = styled(TableCell)(({ theme }) => ({
    fontWeight: "400", fontSize: '12px', color: '#000', padding: '10px 14px 10px 14px',
    border: 'none'
}));

function convertToGanttData(taskData) {
    const ganttTasks = [];

    const parseDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).getTime();
    };

    const processTask = (task, parentId = null) => {
        const startDate = parseDate(task?.baselineStartDate);

        const endDate = parseDate(task?.baselineEndDate);


        ganttTasks.push({
            id: task.activityId,
            name: task.activityName,
            start: startDate,
            end: endDate,
            y: task.percentageComplete !== null ? parseFloat(task.percentageComplete) : 0,
            ...(parentId !== null ? { parent: String(parentId), collapsed: true } : { collapsed: true }),
            completed: {
                amount: parseFloat(task.percentageComplete / 100)
            },
            customFields: task.customFields
        });

        if (task.children && task.children.length > 0) {
            task.children.forEach(child => {
                processTask(child, task.activityId);
            });
        }
    };

    const tasksToProcess = Array.isArray(taskData) ? taskData : [taskData];

    tasksToProcess.forEach(task => {
        processTask(task, null);
    });


    return ganttTasks;
}

export default function Schedule() {
    const newtheme = useTheme();
    const { projectId } = useParams();

    const { data: allSchedule, error, isLoading } = useGetAllScheduleQuery({ projectId });

    const chartOptions = useMemo(() => {

        let options = {
            title: {
                text: "",
            },
            tooltip: {
                pointFormat: '<span>{point.name}</span><br/>Start: {point.start:%e %b %Y}<br/>End: {point.end:%e %b %Y}',
            },
            credits: {
                enabled: false,
            },
            xAxis: {
                currentDateIndicator: false,
                min: new Date().getTime() - (30 * 24 * 60 * 60 * 1000),
                max: new Date().getTime() + (90 * 24 * 60 * 60 * 1000),
            },
            series: [{
                name: 'Tasks',
                data: [],
                dataLabels: {
                    enabled: true,
                    format: '{point.name}',
                    style: {
                        fontSize: '10px'
                    }
                },
            }],

            plotOptions: {
                series: {
                    pointPadding: 0,
                    groupPadding: 0,
                    borderWidth: 0,
                    dataLabels: {
                        enabled: true,
                        format: '{point.name}',
                        style: {
                            color: '#333'
                        }
                    }
                }
            },
        };

        if (!isLoading && allSchedule && allSchedule.data && allSchedule.data.length > 0) {
            const convertedData = convertToGanttData(allSchedule.data[0]);

            options.series[0].data = convertedData;

            if (convertedData.length > 0) {
                const allStarts = convertedData.map(d => d.start).filter(Boolean);
                const allEnds = convertedData.map(d => d.end).filter(Boolean);
                if (allStarts.length > 0) {
                    options.xAxis.min = Math.min(...allStarts) - (7 * 24 * 60 * 60 * 1000);
                }
                if (allEnds.length > 0) {
                    options.xAxis.max = Math.max(...allEnds) + (7 * 24 * 60 * 60 * 1000);
                }
            }
        }

        return options;
    }, [allSchedule, isLoading]);


    return (
        <Box sx={{ width: "100%", border: "1px solid #D8D8D8", mt: 2, borderRadius: "12px", overflow: "hidden" }}>
            <Box sx={{ backgroundColor: "#fff", p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, fontSize: "18px", color: `${newtheme?.customSettings?.themeColor}` }}>
                    Schedule
                </Typography>
            </Box>
            {allSchedule?.data?.length > 0 &&
                <HighchartsReact
                    highcharts={Highcharts}
                    constructorType={"ganttChart"}
                    options={chartOptions}
                />
            }
            {isLoading &&
                <>
                    <Paper sx={{ boxShadow: 'none' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {[...Array(4)].map((_, i) => (
                                            <StyledTableHead key={i}>
                                                <Skeleton variant="text" width={100} height={20} />
                                            </StyledTableHead>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...Array(5)].map((_, rowIdx) => (
                                        <TableRow key={rowIdx}>
                                            {[...Array(4)].map((_, colIdx) => (
                                                <StyledTableRow key={colIdx}>
                                                    <Skeleton variant="text" width={colIdx === 0 ? "700px" : "100%"} height={20} />
                                                </StyledTableRow>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            }
        </Box>
    );
}